import { z } from "zod";
import { MAX_GIFT_ARRAY } from "./giftArrays.js";
import { DEFAULT_GIVING_CART, type PageDoc } from "./pageDoc.js";

/**
 * A/B tests on a landing page (CT-RM-13). Variant A is the control (the page
 * as saved); other variants override the headline, the giving/submit button
 * label, and/or the gift array. Visitors are split by weight (percent) and
 * stay in their variant via a cookie. A visitor counts once per variant
 * (impression) and converts at most once (a completed gift or submission).
 */

export const EXPERIMENT_CONFIDENCES = [90, 95, 99] as const;
export const zExperimentConfidence = z.union([z.literal(90), z.literal(95), z.literal(99)]);
export type ExperimentConfidence = z.infer<typeof zExperimentConfidence>;

export const MAX_VARIANTS = 4;
export const VARIANT_KEYS = ["A", "B", "C", "D"] as const;

export const zVariantOverrides = z
  .object({
    /** replaces the first hero heading (or, without a hero, the first heading block) */
    headline: z.string().trim().min(1).max(300).optional(),
    /** replaces the giving block's button label (or the form's submit label without one) */
    buttonLabel: z.string().trim().min(1).max(80).optional(),
    /** a different fixed gift array (minor units) */
    presets: z.array(z.number().int().min(100).max(100_000_000)).min(1).max(MAX_GIFT_ARRAY).optional(),
    /** switch personalization on/off for this variant (uses the block's personalization settings) */
    personalized: z.boolean().optional(),
    /** CT-RM-31: turn multiple designations (cart) on/off for this variant (uses the block's cart settings) */
    cart: z.boolean().optional(),
  })
  .strict();
export type VariantOverrides = z.infer<typeof zVariantOverrides>;

export const zExperimentVariant = z.object({
  key: z.enum(VARIANT_KEYS),
  name: z.string().trim().min(1).max(80),
  /** traffic share, percent; all variants sum to 100 */
  weight: z.number().int().min(0).max(100),
  overrides: zVariantOverrides.default({}),
});
export type ExperimentVariant = z.infer<typeof zExperimentVariant>;

export const zSaveExperiment = z
  .object({
    name: z.string().trim().min(1).max(120),
    confidence: zExperimentConfidence.default(95),
    /** visitors each variant needs before the test may stop on significance */
    minSample: z.number().int().min(10).max(1_000_000).default(100),
    autoStop: z.boolean().default(true),
    variants: z.array(zExperimentVariant).min(2).max(MAX_VARIANTS),
  })
  .superRefine((e, ctx) => {
    const total = e.variants.reduce((s, v) => s + v.weight, 0);
    if (total !== 100) ctx.addIssue({ code: "custom", path: ["variants"], message: `Traffic split must add up to 100% (it's ${total}%)` });
    e.variants.forEach((v, i) => {
      if (v.key !== VARIANT_KEYS[i]) ctx.addIssue({ code: "custom", path: ["variants", i, "key"], message: `Variant ${i + 1} must be "${VARIANT_KEYS[i]}"` });
      if (i === 0 && Object.keys(v.overrides).length) ctx.addIssue({ code: "custom", path: ["variants", 0], message: "The control (A) shows the page as saved — it can't override anything" });
      if (i > 0 && !Object.keys(v.overrides).length) ctx.addIssue({ code: "custom", path: ["variants", i], message: `Variant ${v.key} doesn't change anything` });
      if (v.weight === 0) ctx.addIssue({ code: "custom", path: ["variants", i, "weight"], message: `Variant ${v.key} gets no traffic` });
    });
  });
export type SaveExperiment = z.infer<typeof zSaveExperiment>;

export const zPromoteExperiment = z.object({ variantKey: z.enum(VARIANT_KEYS).optional() });

export type ExperimentStatus = "draft" | "running" | "completed";
export type ExperimentStopReason = "significance" | "manual" | "promoted";

/** what a variant needs on the page; errors when its overrides have nothing to change */
export function variantTargetErrors(design: Pick<PageDoc, "blocks">, variants: Pick<ExperimentVariant, "key" | "overrides">[]): string[] {
  const has = (t: PageDoc["blocks"][number]["type"]) => design.blocks.some((b) => b.type === t);
  const errors: string[] = [];
  for (const v of variants) {
    const o = v.overrides;
    if (o.headline != null && !has("hero") && !has("heading")) errors.push(`Variant ${v.key} changes the headline, but the page has no hero or heading block`);
    if (o.buttonLabel != null && !has("giving") && !has("form")) errors.push(`Variant ${v.key} changes the button, but the page has no giving or form block`);
    if ((o.presets != null || o.personalized != null) && !has("giving")) errors.push(`Variant ${v.key} changes the gift array, but the page has no giving block`);
    if (o.cart != null && !has("giving")) errors.push(`Variant ${v.key} changes multiple designations, but the page has no giving block`);
  }
  return errors;
}

/** the page as a variant sees it (pure; skips overrides whose target block is gone) */
export function applyVariant(design: PageDoc, overrides: VariantOverrides): PageDoc {
  const blocks = design.blocks.map((b) => ({ ...b }));
  if (overrides.headline != null) {
    const hero = blocks.find((b) => b.type === "hero");
    const heading = blocks.find((b) => b.type === "heading");
    if (hero && hero.type === "hero") hero.heading = overrides.headline;
    else if (heading && heading.type === "heading") heading.text = overrides.headline;
  }
  const giving = blocks.find((b) => b.type === "giving");
  if (overrides.buttonLabel != null) {
    const form = blocks.find((b) => b.type === "form");
    if (giving && giving.type === "giving") giving.buttonLabel = overrides.buttonLabel;
    else if (form && form.type === "form") form.submitLabel = overrides.buttonLabel;
  }
  if (giving && giving.type === "giving") {
    if (overrides.presets) {
      giving.presets = [...overrides.presets];
      // a fixed array test means fixed amounts, unless the variant also asks for personalization
      if (overrides.personalized == null) giving.arrayMode = "fixed";
    }
    if (overrides.personalized != null) giving.arrayMode = overrides.personalized ? "personalized" : "fixed";
    if (overrides.cart != null) giving.cart = { ...DEFAULT_GIVING_CART, ...giving.cart, enabled: overrides.cart };
  }
  return { ...design, blocks };
}

// ---------------------------------------------------------------- statistics

/** standard normal CDF (Abramowitz & Stegun 7.1.26 erf; |error| < 1.5e-7) */
export function normalCdf(z: number): number {
  const x = Math.abs(z) / Math.SQRT2;
  const t = 1 / (1 + 0.3275911 * x);
  const y = 1 - ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-x * x);
  return z >= 0 ? 0.5 * (1 + y) : 0.5 * (1 - y);
}

/**
 * Pooled two-proportion z-test of B against A (two-sided). z > 0 means B
 * converts better. Null when either arm is empty or there's no variance.
 */
export function twoProportionZTest(convA: number, nA: number, convB: number, nB: number): { z: number; pValue: number } | null {
  if (nA <= 0 || nB <= 0) return null;
  const pA = convA / nA;
  const pB = convB / nB;
  const pooled = (convA + convB) / (nA + nB);
  const se = Math.sqrt(pooled * (1 - pooled) * (1 / nA + 1 / nB));
  if (!(se > 0)) return null;
  const z = (pB - pA) / se;
  return { z, pValue: Math.min(1, 2 * (1 - normalCdf(Math.abs(z)))) };
}

export interface VariantCounts {
  key: string;
  visitors: number;
  conversions: number;
  /** CT-RM-31: minor units given by converting submissions (every line of a cart counts once, toward one conversion) */
  raised?: number;
}

export interface VariantResult extends VariantCounts {
  /** conversions / visitors, 0–1; null with no visitors */
  rate: number | null;
  /** relative lift over the control, e.g. 0.25 = +25%; null for the control or when undefined */
  lift: number | null;
  z: number | null;
  pValue: number | null;
  /** 1 − p, 0–1 (the confidence that this variant differs from the control) */
  confidence: number | null;
  /** differs from the control at the test's (multiple-comparison adjusted) threshold */
  significant: boolean;
}

export interface ExperimentEvaluation {
  variants: VariantResult[];
  /** highest conversion rate (ties keep the earlier variant) */
  leaderKey: string | null;
  /** every variant has at least minSample visitors */
  sampleReached: boolean;
  /** the leader is significantly better than every other arm it needs to beat */
  decided: boolean;
  /** decided and the sample is reached — an auto-stopping test should end now */
  shouldStop: boolean;
  /** per-comparison p-value threshold: (1 − confidence) / (variants − 1) (Bonferroni) */
  alpha: number;
}

/** compare each variant to the control (the first); decide whether the test is over */
export function evaluateExperiment(counts: VariantCounts[], confidence: number, minSample: number): ExperimentEvaluation {
  const alpha = (1 - confidence / 100) / Math.max(1, counts.length - 1);
  const control = counts[0];
  const variants: VariantResult[] = counts.map((c, i) => {
    const rate = c.visitors > 0 ? c.conversions / c.visitors : null;
    const controlRate = control && control.visitors > 0 ? control.conversions / control.visitors : null;
    if (i === 0 || !control) return { ...c, rate, lift: null, z: null, pValue: null, confidence: null, significant: false };
    const test = twoProportionZTest(control.conversions, control.visitors, c.conversions, c.visitors);
    return {
      ...c,
      rate,
      lift: rate != null && controlRate ? (rate - controlRate) / controlRate : null,
      z: test?.z ?? null,
      pValue: test?.pValue ?? null,
      confidence: test ? 1 - test.pValue : null,
      significant: !!test && test.pValue < alpha,
    };
  });
  let leader: VariantResult | null = null;
  for (const v of variants) if (v.rate != null && (leader == null || v.rate > (leader.rate ?? -1))) leader = v;
  const sampleReached = counts.length > 1 && counts.every((c) => c.visitors >= minSample);
  // a non-control leader must beat the control; a control leader must beat every variant
  const decided =
    !!leader &&
    (leader.key === control?.key ? variants.slice(1).every((v) => v.significant && (v.z ?? 0) < 0) : leader.significant && (leader.z ?? 0) > 0);
  return { variants, leaderKey: leader?.key ?? null, sampleReached, decided, shouldStop: decided && sampleReached, alpha };
}

// ---------------------------------------------------------------- API shapes

export interface ExperimentRecord {
  id: string;
  pageId: string;
  name: string;
  status: ExperimentStatus;
  confidence: ExperimentConfidence;
  minSample: number;
  autoStop: boolean;
  variants: ExperimentVariant[];
  winnerKey: string | null;
  stopReason: ExperimentStopReason | null;
  startedAt: string | null;
  endedAt: string | null;
  promotedAt: string | null;
  createdAt: string;
  results: ExperimentEvaluation;
}

/** what the public page response says about the visitor's experiment (null when none applies) */
export interface PublicExperimentAssignment {
  id: string;
  variant: string;
}
